import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Search, Upload, Image as ImageIcon, Loader2 } from "lucide-react";
import { ATID } from "@/lib/adminTestIds";
import { formatDateShort } from "@/lib/date";

const TYPE_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "image", label: "Imágenes" },
  { value: "gif", label: "GIF" },
  { value: "svg", label: "SVG" },
  { value: "video", label: "Videos" },
  { value: "audio", label: "Audio" },
  { value: "pdf", label: "PDF" },
];

const ACCEPT = "image/jpeg,image/png,image/webp,image/gif,image/svg+xml,video/mp4,video/webm,audio/mpeg,audio/wav,application/pdf";

/**
 * MediaPicker — dialog that lets the user pick an existing media asset or
 * upload a new one. Calls onSelect(mediaDoc) with the chosen asset.
 */
export default function MediaPicker({ open, onOpenChange, onSelect, accept = "image", title = "Elegir de la biblioteca" }) {
  const [tab, setTab] = useState("library"); // library | upload
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [mimeGroup, setMimeGroup] = useState(accept === "image" ? "image" : "");
  const [selected, setSelected] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    api.adminListMedia({ q: q || undefined, mime_group: mimeGroup || undefined, limit: 60 })
      .then((r) => { setItems(r.items); setTotal(r.total); })
      .catch(() => { setItems([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [q, mimeGroup]);

  useEffect(() => {
    if (!open) return;
    setSelected(null);
    setTab("library");
    load();
  }, [open, load]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [q, mimeGroup, load, open]);

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      let lastId = null;
      for (const file of files) {
        setProgress(0);
        try {
          const doc = await api.adminUploadMedia(file, {}, setProgress);
          lastId = doc.id;
        } catch (e) {
          const d = e?.response?.data?.detail;
          toast.error(typeof d === "string" ? d : `No se pudo subir ${file.name}.`);
        }
      }
      toast.success("Archivos subidos.");
      setTab("library");
      // Reload and pre-select last uploaded
      const r = await api.adminListMedia({ mime_group: mimeGroup || undefined, limit: 60 });
      setItems(r.items);
      setTotal(r.total);
      if (lastId) {
        const found = r.items.find((x) => x.id === lastId);
        if (found) setSelected(found);
      }
    } finally {
      setUploading(false);
      setProgress(0);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const confirm = () => {
    if (!selected) return;
    onSelect?.(selected);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={ATID.mediaPicker} className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-serif">{title}</DialogTitle>
          <DialogDescription>Elegí una imagen ya cargada o subí una nueva.</DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 border-b border-softblue">
          <button
            type="button"
            onClick={() => setTab("library")}
            className={`px-4 py-2 text-xs uppercase tracking-widest font-semibold border-b-2 transition-colors ${
              tab === "library" ? "border-nightblue text-nightblue" : "border-transparent text-textgray hover:text-nightblue"
            }`}
          >
            Biblioteca ({total})
          </button>
          <button
            type="button"
            onClick={() => setTab("upload")}
            className={`px-4 py-2 text-xs uppercase tracking-widest font-semibold border-b-2 transition-colors ${
              tab === "upload" ? "border-nightblue text-nightblue" : "border-transparent text-textgray hover:text-nightblue"
            }`}
          >
            Subir nuevo
          </button>
        </div>

        {tab === "library" ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
              <div className="relative md:col-span-2">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-textgray" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Buscar por nombre, alt o descripción…"
                  className="pl-9 h-9 bg-lightgray border-softblue text-sm"
                />
              </div>
              <select
                value={mimeGroup}
                onChange={(e) => setMimeGroup(e.target.value)}
                className="h-9 bg-lightgray border border-softblue px-3 text-sm text-nightblue"
              >
                {TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>

            <div className="flex-1 overflow-auto -mx-1 px-1">
              {loading ? (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                  {Array.from({ length: 12 }).map((_, i) => (
                    <div key={i} className="aspect-square bg-softblue/40 animate-pulse" />
                  ))}
                </div>
              ) : items.length === 0 ? (
                <div className="p-6 text-center text-sm text-textgray">No hay archivos.</div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                  {items.map((m) => {
                    const isImg = m.mime?.startsWith("image/");
                    const thumb = m.variants?.thumbnail?.url || m.url;
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => setSelected(m)}
                        data-testid={ATID.mediaPickerCard(m.id)}
                        className={`aspect-square bg-lightgray border relative overflow-hidden transition-all ${
                          selected?.id === m.id ? "border-nightblue ring-2 ring-turquoise" : "border-softblue hover:border-nightblue"
                        }`}
                      >
                        {isImg ? (
                          <img src={thumb} alt={m.alt || m.original_name} loading="lazy" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center p-2 text-textgray">
                            <span className="text-[10px] uppercase tracking-widest font-mono">{m.mime.split("/")[1]}</span>
                            <span className="mt-1 text-[10px] text-center line-clamp-2">{m.original_name}</span>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {selected && (
              <div className="border-t border-softblue pt-3 mt-3 flex items-center gap-3 text-sm">
                <img src={selected.variants?.thumbnail?.url || selected.url} alt="" className="w-14 h-14 object-cover border border-softblue" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-blueblack truncate">{selected.original_name}</div>
                  <div className="text-xs text-textgray">
                    {selected.mime} · {selected.width && selected.height ? `${selected.width}×${selected.height}` : "—"} · {(selected.size / 1024).toFixed(1)} KB
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="py-4">
            <div className="border border-dashed border-softblue bg-lightgray p-8 text-center">
              <ImageIcon className="w-10 h-10 mx-auto text-textgray" />
              <p className="mt-2 text-sm text-textgray">Arrastrá archivos aquí o seleccionalos.</p>
              <Input
                ref={fileRef}
                type="file"
                multiple
                accept={ACCEPT}
                onChange={(e) => uploadFiles(Array.from(e.target.files || []))}
                className="mt-4 mx-auto max-w-md bg-white border-softblue"
              />
              <p className="mt-3 text-[11px] text-textgray">
                Formatos: JPG, PNG, WebP, GIF, SVG, MP4, WebM, MP3, WAV, PDF. Tamaño máximo: 15 MB (imágenes), 100 MB (video), 30 MB (audio), 20 MB (PDF).
              </p>
            </div>
            {uploading && (
              <div className="mt-4">
                <div className="text-xs text-textgray mb-1">Subiendo… {progress}%</div>
                <Progress value={progress} />
              </div>
            )}
          </div>
        )}

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none">Cancelar</Button>
          <Button
            data-testid={ATID.mediaPickerConfirm}
            onClick={confirm}
            disabled={!selected}
            className="bg-nightblue hover:bg-blueblack text-white rounded-none uppercase text-xs tracking-widest font-semibold"
          >
            Usar seleccionada
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
